import type { ReactNode } from "react";
import {
  NOMES_DOS_DIAS,
  NOMES_DOS_DIAS_LONGOS,
  NOMES_DOS_MESES,
  dataBr,
  diaDaSemana,
  diaDe,
  glifoDoAjuste,
  fraseDoAjuste,
  type CelulaDoMes,
  type Vencimento,
} from "../lib/calendarioFiscal";
import { regimeCurto, rotuloDoSetor, type Setor } from "../lib/calendario";

// As peças do Calendário Tributário. Só apresentação: nenhuma decide data, nem
// acesso, nem regime — tudo isso chega pronto.
//
// ---------------------------------------------------------------------------
// A cabine é o único território navy da tela, e nada semântico entra nela
// ---------------------------------------------------------------------------
// Dentro de `painel-navy` o fundo é escuro nos DOIS temas, então `text-tinta`
// clareia no escuro e vira branco sobre branco — a cicatriz dos cards em escada
// do login. Aqui dentro só entra cor de MARCA (white, navy-mute, laranja,
// laranja-claro, sucesso-claro) ou fixa. Os chips de filtro e o ponto de setor,
// que são semânticos, ficam DE FORA da cabine de propósito: levá-los para lá
// exigiria uma prop `sobreNavy` em componente compartilhado, e a prova de
// contraste da casa não alcançaria os dois estados.
//
// A exceção com nome: as três cores de setor são redefinidas pelo próprio
// `painel-navy` no `index.css`, então se um dia um ponto entrar ali ele já pega o
// tom claro sozinho, nos dois temas.

const CORES_DE_SETOR: Record<Setor, string> = {
  fiscal: "bg-setor-fiscal",
  contabil: "bg-setor-contabil",
  dp: "bg-setor-dp",
};

/**
 * O ponto colorido do setor.
 *
 * Cor sozinha nunca carrega informação: o ponto é `aria-hidden` e vem com um
 * texto só para leitor de tela. E as três cores saem de TOKEN, não de cor de
 * marca — medido em 25/08, `bg-azul` dava 1,07 : 1 na superfície escura e
 * `bg-laranja` dava 2,13 : 1 na clara. Não existe cor fixa da marca que passe
 * 3 : 1 nos dois fundos.
 */
export function PontoDeSetor({ setor, comRotulo = true }: { setor: Setor; comRotulo?: boolean }) {
  return (
    <>
      <span
        className={`inline-block size-2 shrink-0 rounded-full ${CORES_DE_SETOR[setor]}`}
        aria-hidden="true"
      />
      {comRotulo && <span className="sr-only">{rotuloDoSetor(setor)}: </span>}
    </>
  );
}

// ---------------------------------------------------------------------------
// A cabine: navegação, fatos, grade e feriados
// ---------------------------------------------------------------------------

/**
 * A navegação de mês: passo a passo E salto direto.
 *
 * O salto não é enfeite. Com só as setas, consultar março de 2030 custaria 46
 * cliques — e num calendário tributário isso é uso normal (declaração atrasada,
 * planejamento, conferência de prazo antigo). É justamente o alcance que a
 * ferramenta ganhou ao guardar a REGRA em vez da data; esconder isso atrás de 46
 * cliques o desperdiçaria. A faixa vai de 2000 a 2100 porque a regra vale para
 * qualquer ano, e limitar a lista recriaria na tela o prazo de validade que o
 * porte existe para eliminar.
 *
 * Vestida de navy à mão (borda e texto de marca), e não com os tokens
 * semânticos: está dentro da cabine.
 */
export function NavegacaoDeMes({
  ano,
  mes,
  onAnterior,
  onSeguinte,
  onHoje,
  onIrPara,
}: {
  ano: number;
  mes: number;
  onAnterior: () => void;
  onSeguinte: () => void;
  onHoje: () => void;
  onIrPara: (ano: number, mes: number) => void;
}) {
  const seta =
    "flex size-10 shrink-0 items-center justify-center rounded-xl border border-navy-mute/25 bg-white/5 text-lg text-white transition hover:border-laranja hover:bg-white/10 focus-visible:border-laranja focus-visible:outline-none";
  const salto =
    "h-10 rounded-xl border border-navy-mute/25 bg-white/5 px-2 font-display text-sm font-semibold text-white transition hover:border-laranja focus:border-laranja focus:outline-none";
  const anos: number[] = [];
  for (let a = 2000; a <= 2100; a += 1) anos.push(a);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" onClick={onAnterior} className={seta} aria-label="Mês anterior">
        <span aria-hidden="true">‹</span>
      </button>
      {/* O h2 visível é o par de selects; este existe para o leitor de tela ter
          um título de região, e para a prova achar o mês por texto. */}
      <h2 className="sr-only">
        {NOMES_DOS_MESES[mes - 1]} de {ano}
      </h2>
      <select
        className={salto}
        value={mes}
        onChange={(e) => onIrPara(ano, Number(e.target.value))}
        aria-label="Mês"
      >
        {NOMES_DOS_MESES.map((m, i) => (
          <option key={m} value={i + 1}>
            {m}
          </option>
        ))}
      </select>
      <select
        className={salto}
        value={ano}
        onChange={(e) => onIrPara(Number(e.target.value), mes)}
        aria-label="Ano"
      >
        {anos.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
      <button type="button" onClick={onSeguinte} className={seta} aria-label="Mês seguinte">
        <span aria-hidden="true">›</span>
      </button>
      <button
        type="button"
        onClick={onHoje}
        className="rounded-xl px-3 py-2 text-sm font-semibold text-laranja-claro transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40"
      >
        Hoje
      </button>
    </div>
  );
}

/** Um dos três fatos da cabine, em cartão de vidro sobre o navy. */
export function TileDeFato({
  rotulo,
  valor,
  apoio,
  className = "",
}: {
  rotulo: string;
  valor: ReactNode;
  apoio?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`tv-card p-3 ${className}`}>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-navy-mute">
        {rotulo}
      </p>
      <p className="pt-1 font-display text-xl leading-tight font-bold text-white">{valor}</p>
      {apoio && <p className="pt-0.5 text-xs leading-snug text-navy-mute">{apoio}</p>}
    </div>
  );
}

/** O que a grade precisa saber de um dia, além do que `gradeDoMes` já dá. */
export type DiaComObrigacoes = CelulaDoMes & {
  quantidade: number;
  /** Quantos pares (cliente, obrigação) do dia ainda faltam. Nulo sem carteira. */
  pendentes: number | null;
};

/**
 * A grade do mês, dentro da cabine — o MAPA, não a superfície de trabalho.
 *
 * Ela foi rebaixada por medição, não por gosto: nos doze meses de 2026 o mês tem
 * em média 14,5 vencimentos concentrados em 5,3 dias distintos, então **82% das
 * células estão vazias**. Como protagonista, ela gastava metade da tela para
 * marcar cinco dias. O que só ela dá — e por isso fica — é a FORMA do mês: quais
 * dias são pesados, onde caem os fins de semana e os feriados, e qual dia já
 * está fechado.
 *
 * A célula tem altura FIXA (`h-11`), não `min-h`: com piso e conteúdo variável a
 * semana cresce e a grade pula quando o aluno navega, e o dia que ele estava
 * olhando troca de lugar. Seis semanas × 44px + cinco vãos de 4px = 284px,
 * sempre, em qualquer mês.
 *
 * Clicar num dia FILTRA a lista **e rola até ela** — desde 28/08/2026.
 *
 * A decisão anterior era não rolar, e o motivo escrito aqui era que rolar
 * exigiria "cravar a altura do `Topo` num `scroll-mt` — acoplamento silencioso
 * que quebra quando o topo muda". A objeção estava certa e a conclusão não:
 * **não é preciso cravar nada**, porque `--cf-topo` existe exatamente para isso
 * (o comentário dela no `index.css` diz "se o Topo mudar de altura, muda AQUI e
 * todo mundo acompanha"). Quem lê a variável é `lib/rolarPara.ts`.
 *
 * O outro argumento continua valendo e virou código: num mês com um vencimento
 * só a lista JÁ está visível, e `rolarAte` não faz nada quando o alvo já está
 * inteiro na tela. Rolar o que já se vê é movimento gratuito.
 *
 * Quem achou o defeito na tela foi Victor, junto com o mesmo problema no Mapa de
 * Concursos (clicar num estado) e no Admin › Alunos (escolher um aluno). Nos
 * três, o conteúdo trocava fora do campo de visão — e o modo de falha é o pior:
 * a pessoa clica, não vê nada mudar, e conclui que o clique não funcionou.
 */
export function GradeDoMes({
  dias,
  selecionado,
  onSelecionar,
}: {
  dias: DiaComObrigacoes[];
  selecionado: string | null;
  onSelecionar: (data: string) => void;
}) {
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 pb-1">
        {NOMES_DOS_DIAS.map((d) => (
          <div
            key={d}
            className="py-0.5 text-center text-[0.625rem] font-semibold uppercase tracking-wide text-navy-mute"
          >
            {d}
          </div>
        ))}
      </div>
      {/*
        `data-grade="dias"` existe para a prova poder CONTAR as 42 células com
        escopo. Sem ele, `[aria-pressed]` pegava também os chips de filtro e dava
        50 — uma asserção que passaria por acaso se a grade tivesse 41 células e
        houvesse 9 chips. É a cicatriz dos sete rótulos iguais da calculadora.
      */}
      <div data-grade="dias" className="grid grid-cols-7 gap-1">
        {dias.map((c) => {
          const escolhido = c.data === selecionado;
          // Dia fora do mês some por PESO e ausência de ladrilho, não por
          // `opacity`: texto a 40% de opacidade é o defeito que o comentário do
          // `--cf-superficie-2` registra — a descrição caía para 2,88 : 1.
          const fundo = !c.doMes
            ? "text-navy-mute/45"
            : c.feriado
              ? "bg-laranja/20 text-laranja-claro"
              : c.fimDeSemana
                ? "bg-white/5 text-navy-mute"
                : "bg-white/10 text-white";
          const borda = escolhido
            ? "border-laranja ring-2 ring-laranja/40"
            : c.hoje
              ? "border-laranja-claro"
              : "border-transparent";
          return (
            <button
              key={c.data}
              type="button"
              onClick={() => onSelecionar(c.data)}
              aria-pressed={escolhido}
              aria-label={
                `${dataBr(c.data)}` +
                (c.feriado ? ` — ${c.feriado.nome}` : "") +
                (c.quantidade === 0
                  ? " — nada a vencer"
                  : ` — ${c.quantidade} ${c.quantidade === 1 ? "obrigação" : "obrigações"}`) +
                (c.pendentes === 0 && c.quantidade > 0 ? " — tudo entregue" : "")
              }
              className={`relative flex h-11 flex-col items-center justify-center rounded-lg border transition hover:border-laranja focus-visible:border-laranja focus-visible:outline-none ${fundo} ${borda}`}
            >
              <span className={`text-xs ${c.hoje ? "font-bold" : "font-medium"}`}>{c.dia}</span>
              {c.quantidade > 0 && (
                <span className="flex items-center gap-0.5 text-[0.625rem] leading-none font-bold">
                  {/* A contagem é a única informação que só a grade dá: quais
                      dias são pesados. E o ✓ é o único sinal de "este dia está
                      fechado" sem abrir a lista. */}
                  {c.pendentes === 0 ? (
                    <span className="text-sucesso-claro" aria-hidden="true">
                      ✓
                    </span>
                  ) : (
                    <span className="text-laranja-claro">{c.quantidade}</span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A lista: rubrica do dia, linha da obrigação, selo do ajuste
// ---------------------------------------------------------------------------

/** Etiqueta pequena. */
export function Etiqueta({
  children,
  tom = "neutro",
}: {
  children: ReactNode;
  tom?: "neutro" | "alerta" | "sucesso" | "erro";
}) {
  const cor =
    tom === "alerta"
      ? "bg-alerta-fundo text-alerta-tinta"
      : tom === "sucesso"
        ? "bg-aviso-fundo text-aviso-tinta"
        : tom === "erro"
          ? "bg-erro-fundo text-erro-tinta"
          : "bg-chip text-chip-tinta";
  return (
    <span className={`rounded-md px-1.5 py-0.5 text-[0.6875rem] font-semibold ${cor}`}>
      {children}
    </span>
  );
}

/**
 * A rubrica do dia — a data dita UMA vez.
 *
 * Medido: com 15 obrigações em 6 datas, a data era dita 15 vezes na lista mais 5
 * na grade. Agora são 6 rubricas, e o dia da semana sai de 15 para 6. É a
 * cicatriz do IR Pro (34 assuntos em dois lugares, 8.249px de altura) aplicada
 * ao eixo do tempo.
 *
 * O selo "dia não útil" na rubrica é o que torna VISÍVEL o defeito herdado: em
 * junho de 2026 o Dirbi vence num sábado, e a rubrica do dia 20 diz isso.
 */
export function RubricaDoDia({
  data,
  quantos,
  ehDiaUtil,
  jaPassou,
}: {
  data: string;
  quantos: number;
  ehDiaUtil: boolean;
  jaPassou: boolean;
}) {
  const semana = NOMES_DOS_DIAS_LONGOS[diaDaSemana(data)];
  return (
    <h3
      id={`dia-${data}`}
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-5 pb-2 first:pt-0"
    >
      <time
        dateTime={data}
        className={`font-display text-2xl leading-none font-bold ${
          jaPassou ? "text-cinza" : "text-titulo"
        }`}
      >
        {String(diaDe(data)).padStart(2, "0")}
      </time>
      <span className="text-sm font-semibold text-tinta">{semana}</span>
      <span className="text-xs text-cinza">
        · {quantos} {quantos === 1 ? "prazo" : "prazos"}
      </span>
      {!ehDiaUtil && (
        <span className="rounded-full bg-alerta-fundo px-2 py-0.5 text-[0.6875rem] font-semibold text-alerta-tinta">
          {semana} — dia não útil
        </span>
      )}
      {jaPassou && (
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-cinza">
          já venceu
        </span>
      )}
    </h3>
  );
}

/**
 * O selo do ajuste: `sáb 20 → seg 22`.
 *
 * Esta é a tese do produto virando um glifo. A frase inteira ("o dia 20 caiu num
 * sábado, então o prazo foi prorrogado para 22/06/2026") vai no `title` e no
 * `sr-only`: quem varre a tela lê o glifo, quem usa leitor recebe a frase.
 *
 * O texto sai de `glifoDoAjuste`/`fraseDoAjuste`, que leem a `Derivacao` nascida
 * dentro de `vencimentoDaObrigacao`. Remontar aqui — "se a data difere do dia da
 * regra, então…" — seria a segunda definição do mesmo fato, e a `000091` já
 * mostrou o preço: a explicação passa a contradizer o número.
 */
export function SeloDeAjuste({ vencimento }: { vencimento: Vencimento }) {
  const glifo = glifoDoAjuste(vencimento);
  if (!glifo) return null;
  const frase = fraseDoAjuste(vencimento) ?? "";
  const anteciou = vencimento.derivacao.sentido === "antecipou";
  return (
    <span
      title={frase}
      className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[0.6875rem] font-semibold ${
        anteciou
          ? "border-alerta-tinta/30 bg-alerta-fundo text-alerta-tinta"
          : "border-aviso-tinta/30 bg-aviso-fundo text-aviso-tinta"
      }`}
    >
      <span aria-hidden="true">{glifo}</span>
      <span className="sr-only">{frase}</span>
    </span>
  );
}

/** Chips de filtro com CONTAGEM e o ponto de setor dentro. */
export function ChipsDeFiltro<T extends string>({
  id,
  rotulo,
  opcoes,
  ativos,
  onAlternar,
}: {
  id: string;
  rotulo: string;
  opcoes: { valor: T; rotulo: string; quantos?: number; setor?: Setor }[];
  ativos: Set<T>;
  onAlternar: (v: T) => void;
}) {
  return (
    <section role="group" aria-labelledby={id} className="flex min-w-0 items-center gap-2">
      {/*
        `<section role="group" aria-labelledby>` e não `<fieldset><legend>`: a
        especificação posiciona o legend SOBRE a borda do fieldset, e num card
        arredondado o título sai flutuando fora da caixa — visto na tela em 19/08.
      */}
      <h3 id={id} className="shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wide text-cinza">
        {rotulo}
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {opcoes.map((o) => {
          const ligado = ativos.has(o.valor);
          return (
            <button
              key={o.valor}
              type="button"
              onClick={() => onAlternar(o.valor)}
              aria-pressed={ligado}
              // `text-azul` sobre `bg-laranja`, nunca `text-navy-ink`: #f1f1f2
              // sobre #ff9933 dá 2,13 : 1 e reprova — foi o que eu havia escrito
              // aqui. Azul sobre laranja dá 6,84 : 1, e vale nos dois temas
              // porque as duas cores são de marca. É a regra da casa para o CTA.
              className={`inline-flex items-center gap-1.5 rounded-full border-[1.5px] px-2.5 py-1.5 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/25 ${
                ligado
                  ? "border-laranja bg-laranja font-semibold text-azul"
                  : "border-borda bg-superficie text-cinza hover:border-laranja"
              }`}
            >
              {/* O ponto DENTRO do chip mata o componente `LegendaDeSetores`: a
                  legenda era a mesma informação dita duas vezes na mesma tela. */}
              {o.setor && <PontoDeSetor setor={o.setor} comRotulo={false} />}
              {o.rotulo}
              {typeof o.quantos === "number" && (
                <span className={ligado ? "text-azul/70" : "text-cinza/70"}>{o.quantos}</span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function EtiquetasDeRegime({ geral, regimes }: { geral: boolean; regimes: string[] }) {
  if (geral) return <Etiqueta>Todos os regimes</Etiqueta>;
  return (
    <>
      {regimes.map((r) => (
        <Etiqueta key={r}>{regimeCurto(r)}</Etiqueta>
      ))}
    </>
  );
}
